import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import { GeneralErrorMessage } from '@core/constants/messages';
import { MemberPacService } from 'src/app/core';
import { Interruption, InterruptionPayload } from '@core/model';
import { InterruptionActionTypes, InterruptionConfirmButtonLabels } from '@shared/enumeration';
import { MessageService } from 'primeng/api';
import { finalize, Observable } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'interrupted-stay-tab-pac',
    templateUrl: './interrupted-stay-tab-pac.component.html',
    styleUrls: ['./interrupted-stay-tab-pac.component.scss'],
    standalone: false
})
export class InterruptedStayTabPacComponent implements OnDestroy {
  @Input() interruptedStay: Interruption;
  @Input() tenantId: string;
  @Input() productId: string;
  @Input() isExternal: boolean;

  @Output() cancelProcess = new EventEmitter<void>();
  @Output() startAuthRequest = new EventEmitter<void>();

  isInterruptedFormInvalid = true;
  interruptedStayFormValue = null;

  confirmButtonName = InterruptionConfirmButtonLabels.Confirm;
  actionType = InterruptionActionTypes.Proceed;
  isLoading = false;

  private readonly subs = new SubSink();

  constructor(
    private memberPacService: MemberPacService,
    private messageService: MessageService,
    private router: Router,
  ) {}

  onFormChange(event: { invalid: boolean, value: any }): void {
    this.isInterruptedFormInvalid = event.invalid;
    this.interruptedStayFormValue = event.value;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onCancel(): void {
    this.cancelProcess.emit();
  }

  onProceed(): void {
    switch (this.actionType) {
      case InterruptionActionTypes.Proceed:
        this.startAuthRequest.emit();
        break;
      case InterruptionActionTypes.AuthRequestWithInterruption:
        this.subs.sink = this.saveInterruption().subscribe({
          next: () => this.startAuthRequest.emit(),
          error: () => this.handleError(),
        });
        break;
      case InterruptionActionTypes.SimpleSave:
        this.subs.sink = this.saveInterruption().subscribe({
          next: () => this.navigateToServiceLine(),
          error: () => this.handleError(),
        });
        break;
      case InterruptionActionTypes.CSRPendingSl:
        this.subs.sink = this.saveInterruption().subscribe({
          next: () => {
            this.messageService.add({
              key: 'csr-redirect',
              severity: 'error',
              summary: 'Submission Not Yet Available',
              detail: 'You cannot submit a continued stay request until the pending service line in this authorization is approved or denied.',
              life: 5000,
            });
          },
          error: () => this.handleError(),
        });
        break;
      case InterruptionActionTypes.CSRUnavailableRange:
        this.subs.sink = this.saveInterruption().subscribe({
          next: () => {
            this.messageService.add({
              key: 'csr-redirect',
              severity: 'error',
              summary: 'Submission Not Available',
              detail: 'You cannot submit a continued stay request because of existing service line assigned for the same dates.',
              life: 5000,
            });
          },
          error: () => this.handleError(),
        });
        break;
      case InterruptionActionTypes.CSRWithInterruption:
        this.subs.sink = this.saveInterruption().subscribe({
          next: () => this.navigateToCSR(),
          error: () => this.handleError(),
        });
        break;
    }
  }

  interruptedStayActionChanged(action: InterruptionActionTypes): void {
    this.actionType = action;

    switch (this.actionType) {
      case InterruptionActionTypes.Proceed:
      case InterruptionActionTypes.AuthRequestWithInterruption:
      case InterruptionActionTypes.SimpleSave:
        this.confirmButtonName = InterruptionConfirmButtonLabels.Confirm;
        break;
      case InterruptionActionTypes.CSRWithInterruption:
      case InterruptionActionTypes.CSRUnavailableRange:
      case InterruptionActionTypes.CSRPendingSl:
        this.confirmButtonName = InterruptionConfirmButtonLabels.ContStayRequest;
        break;
    }
  }

  navigateToServiceLine(): void {
    this.router.navigate([this.tenantId, 'pac-im', 'auth-dashboard', this.interruptedStay.authorizationNumber], {
      state: { targetWidget: 'service-lines-pacim' },
    });
  }

  private saveInterruption(): Observable<void> {
    this.isLoading = true;
    const payload = this.serializeForm();

    return this.memberPacService.saveInterruption(this.tenantId, this.interruptedStay.serviceLineId, payload).pipe(
      finalize(() => this.isLoading = false),
    );
  }

  private serializeForm(): InterruptionPayload {
    const formValue = this.interruptedStayFormValue;

    return {
      startDate: formatDate(formValue.leftSNFDate, 'yyyy-MM-dd', 'en-US'),
      endDate: formatDate(formValue.returnSNFDate, 'yyyy-MM-dd', 'en-US'),
      disposition: formValue.interruptedDisposition.value,
    };
  }

  private handleError(): void {
    this.messageService.add({
      key: 'interruption',
      severity: 'error',
      summary: 'Error',
      detail: GeneralErrorMessage,
      life: 5000,
    });
  }

  private navigateToCSR(): void {
    this.router.navigate([this.tenantId, 'pac-im', 'continued-stay', this.interruptedStay.authorizationNumber], {
      state: { interruption: true, interuptedLineId: this.interruptedStay.serviceLineId},
    });
  }
}
